import { useMemo } from 'react'
import { useAppStore } from '@forwardflow/store/appStore'
import {
   useGetFubStages,
   useGetPeopleTags,
} from '@forwardflow/queries/onboarding.queries'
import {
   useFubPeopleSources,
   useFubPonds,
} from '@forwardflow/queries/fub.queries'
import {
   DEMO_LEAD_SOURCES,
   DEMO_PONDS,
   DEMO_STAGES,
   DEMO_TAGS,
} from '@forwardflow/features/campaign-v2/utils/demo-data'
import { isFreeFlow } from '@forwardflow/features/campaign-v2/utils/demo-mode'

export interface IOption {
   value: string
   label: string
}

/**
 * What a dynamic audience can be filtered by, from Follow Up Boss:
 * sources, stages, tags and ponds. In free flow it is the demo account.
 */
export const useAudienceVocabulary = () => {
   const { currentOrganization } = useAppStore()
   const freeFlow = isFreeFlow()
   const orgId = freeFlow ? '' : currentOrganization || ''

   const { data: sourcesData, isLoading: l1 } = useFubPeopleSources(
      orgId,
      1,
      500,
      Boolean(orgId)
   )
   const { data: stagesData, isLoading: l2 } = useGetFubStages(
      orgId,
      500,
      1,
      Boolean(orgId)
   )
   const { data: tagsData, isLoading: l3 } = useGetPeopleTags(
      orgId,
      1,
      1000,
      Boolean(orgId)
   )
   const { data: pondsData, isLoading: l4 } = useFubPonds(
      orgId,
      1,
      500,
      Boolean(orgId)
   )

   const sources: IOption[] = useMemo(() => {
      const rows = (sourcesData || []) as Array<{ leadSource: string }>
      const list = rows.length ? rows : freeFlow ? DEMO_LEAD_SOURCES : []
      return list
         .filter((s) => s.leadSource)
         .map((s) => ({ value: s.leadSource, label: s.leadSource }))
   }, [sourcesData, freeFlow])

   const stages: IOption[] = useMemo(() => {
      const rows = (stagesData?.data || []) as Array<{
         stageId: number
         stageName: string
      }>
      if (rows.length)
         return rows
            .filter((s) => s.stageName)
            .map((s) => ({ value: s.stageName, label: s.stageName }))
      return freeFlow
         ? DEMO_STAGES.map((s) => ({ value: s.name, label: s.name }))
         : []
   }, [stagesData, freeFlow])

   const tags: IOption[] = useMemo(() => {
      const rows = (tagsData?.data || []) as Array<{ tagName: string }>
      if (rows.length)
         return rows
            .filter((t) => t.tagName)
            .map((t) => ({ value: t.tagName, label: t.tagName }))
      return freeFlow
         ? DEMO_TAGS.map((t) => ({ value: t.name, label: t.name }))
         : []
   }, [tagsData, freeFlow])

   const ponds: IOption[] = useMemo(() => {
      const rows = (pondsData || []) as Array<Record<string, unknown>>
      const list = rows.length
         ? rows
         : freeFlow
           ? (DEMO_PONDS as unknown as Array<Record<string, unknown>>)
           : []
      return list
         .map((p) => ({
            value: String(p.pondId ?? p.id ?? ''),
            label: String(p.pondName ?? p.name ?? p.pondId ?? p.id ?? ''),
         }))
         .filter((p) => p.value)
   }, [pondsData, freeFlow])

   return { sources, stages, tags, ponds, loading: l1 || l2 || l3 || l4 }
}
